/**
 * #577 lane A (L19) — the optional sample week: read it, keep it, clear it.
 *
 *   GET    /api/onboarding/samples   what example quests this family has, and whether a week may be kept
 *   POST   /api/onboarding/samples   "Keep a sample week" (idempotent: a second call returns the same set)
 *   DELETE /api/onboarding/samples   the one-tap clear (idempotent; refused if an example was used)
 *
 * Every answer is CHECKED before anyone is told anything. A 2xx that names another family, another
 * set, duplicate ids or counts that do not add up is `SampleWeekUnconfirmed`, never a success: the
 * screen then says "we could not confirm" rather than "removed". Parent re-check prompts and CSRF
 * are handled by the shared `request` in `client.js`.
 *
 * `send` is injectable so a test can steer one request without mocking a module.
 */
import { ApiError, request } from './client.js';

export const SAMPLE_SET = 'sample-week-v1';
const PATH = '/api/onboarding/samples';

/** A 2xx this client could not trust. The outcome is UNKNOWN, not refused. */
export class SampleWeekUnconfirmed extends Error {
  constructor(message) {
    super(message);
    this.name = 'SampleWeekUnconfirmed';
  }
}

const isPositiveInt = (v) => Number.isInteger(v) && v > 0;
// The server lowercases the tenant uuid it answers with (`onboarding.service.js`
// `actorFromSession`). The caller's copy is lowercased ONCE here, so an upper-case id held by a
// caller is not mistaken for another family (review L-2).
const sameFamily = (answered, familyId) => !familyId || answered === String(familyId).toLowerCase();
const unique = (list) => new Set(list).size === list.length;

/** The family's example state, or `null` when the body cannot be trusted. */
export function toSampleState(body, familyId) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  if (body.sampleSet !== SAMPLE_SET) return null;
  if (!sameFamily(body.tenantId, familyId)) return null;
  if (!Array.isArray(body.samples) || body.sampleCount !== body.samples.length) return null;
  const samples = [];
  for (const s of body.samples) {
    if (!s || !isPositiveInt(s.id) || typeof s.name !== 'string' || typeof s.hasHistory !== 'boolean') return null;
    samples.push({ id: s.id, name: s.name, hasHistory: s.hasHistory });
  }
  if (!unique(samples.map((s) => s.id))) return null;
  if (typeof body.onboardingComplete !== 'boolean' || typeof body.canKeep !== 'boolean') return null;
  return { familyId: body.tenantId, samples, onboardingComplete: body.onboardingComplete, canKeep: body.canKeep };
}

/** A clear receipt, or `null` when it cannot be trusted. */
export function toClearReceipt(body, familyId) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  if (body.sampleSet !== SAMPLE_SET || !sameFamily(body.tenantId, familyId)) return null;
  const ids = body.deletedIds;
  if (!Array.isArray(ids) || !ids.every(isPositiveInt) || !unique(ids)) return null;
  if (body.deletedCount !== ids.length || body.remainingCount !== 0) return null;
  if (body.outcome === 'removed' && ids.length > 0) return { outcome: 'removed', deletedIds: ids };
  if (body.outcome === 'already_absent' && ids.length === 0) return { outcome: 'already_absent', deletedIds: [] };
  return null;
}

/** @throws ApiError on a refusal or a transport failure, SampleWeekUnconfirmed on an untrusted 2xx. */
export async function fetchSampleWeek(familyId, { send = request } = {}) {
  const state = toSampleState(await send(PATH), familyId);
  if (!state) throw new SampleWeekUnconfirmed('The example quests could not be read');
  return state;
}

/** Keep a sample week. Resolves with the state plus `outcome` ('created' | 'already_kept'). */
export async function keepSampleWeek(familyId, { send = request } = {}) {
  const body = await send(PATH, { method: 'POST', body: {} });
  const state = toSampleState(body, familyId);
  const outcome = body && body.outcome;
  if (!state || state.samples.length === 0 || (outcome !== 'created' && outcome !== 'already_kept')) {
    throw new SampleWeekUnconfirmed('The server did not confirm the sample week');
  }
  return { ...state, outcome };
}

/** The one-tap clear. Resolves only with a trusted receipt. */
export async function clearSampleWeek(familyId, { send = request } = {}) {
  const receipt = toClearReceipt(await send(PATH, { method: 'DELETE' }), familyId);
  if (!receipt) throw new SampleWeekUnconfirmed('The server did not confirm the removal');
  return receipt;
}

/**
 * Was the request REFUSED (nothing changed, the server said why) or is the outcome UNKNOWN?
 * A 4xx is a refusal. A network failure, a 5xx or an untrusted 2xx is unknown: the write may have
 * landed, so the screen must not say "nothing changed".
 *
 * @returns {{kind: 'refused', status: number, code: string|null, blockedIds: number[]} | {kind: 'unknown'}}
 */
export function classifySampleError(error) {
  // Status 0 is `client.js` refusing BEFORE sending (no CSRF cookie, no session): nothing left here.
  if (error instanceof ApiError && error.status === 0) return { kind: 'refused', status: 0, code: 'NO_SESSION', blockedIds: [] };
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    const details = error.body && typeof error.body === 'object' ? error.body.details : null;
    const code = (details && typeof details.code === 'string' ? details.code : null)
      ?? (error.body && typeof error.body.code === 'string' ? error.body.code : null);
    const blockedIds = details && Array.isArray(details.blockedSampleIds) ? details.blockedSampleIds.filter(isPositiveInt) : [];
    return { kind: 'refused', status: error.status, code, blockedIds };
  }
  return { kind: 'unknown' };
}
